<script setup lang="ts">
import { useI18n } from 'petite-vue-i18n';
import { TEXT } from '../../configs/text';
import { ClassViewType } from '../../types/class';
import { computed } from 'vue';
import { useClassStore, useUserStore } from '../../stores';
import goldMedalIcon from '../../assets/icons/medal_gold.svg'
import silverMedalIcon from '../../assets/icons/medal_silver.svg'
import bronzeMedalIcon from '../../assets/icons/medal_bronze.svg'
import cupIcon from '../../assets/icons/cup.svg'

interface Props {
    id?: number
    type?: ClassViewType
    rank?: number
    name?: string
    info?: string
    point?: number
}
const { id, type, rank = 0, name = '', info = '', point = 0 } = defineProps<Props>()

const user = useUserStore()
const classStore = useClassStore()

const isHighlighted = computed<boolean>(() => {
    if (type === ClassViewType.student) {
        return user.userInfo?.id === id
    }
    if (type === ClassViewType.group) {
        return classStore.selectedClassDetails?.student_group_id === id
    }

    return false
})

const iconSrc = computed<string>(() => {
    if (rank < 1) return '';

    const medalMap = [goldMedalIcon, silverMedalIcon, bronzeMedalIcon]
    const cupMap = [cupIcon]

    if (type === ClassViewType.student) {
        return medalMap[rank - 1] || ''
    }

    if (type === ClassViewType.group) {
        return cupMap[rank - 1] || ''
    }

    return ''
})

const { t } = useI18n()
</script>

<template>
    <div v-if="!rank && !name && !info && !point" class="flex items-center justify-center text-center p-3 font-bold text-lg bg-neutral-200 rounded-xl h-16">
        {{ t(TEXT.no_ranking) }}
    </div>
    <div v-else class="flex items-center gap-3 lg:gap-5 py-3 px-4 lg:px-8 border-2 border-neutral-200 rounded-xl h-20 font-bold lg:text-lg mb-3" :class="{
        '!bg-sky-300': rank === 1,
        '!border-sky-400': rank === 1 || isHighlighted
    }">
        <div class="flex items-center justify-center shrink-0">
            <component :is="iconSrc" v-if="iconSrc" class="w-8 lg:w-11" />
            <div v-else class="w-8 lg:w-11 text-center">{{ rank || '--' }}</div>
        </div>
        <div>
            {{ name }}
            <span v-if="info" class="text-sm font-normal">{{ info }}</span>
        </div>
        <div class="ml-auto shrink-0">{{ point }} {{ t(TEXT.pts) }}</div>
    </div>
</template>